import { atom, selector } from "recoil";
import { DateRangeType } from "types/dateRange";
import axios from "../utils/axios";
import realAxios from "axios";
import { AssetType } from "types/common";
import { reducePriceValue } from "@api/assetLegacy";

// const splitBaseDate = (value) => value.map((v) => v.split("T")[0]);

export const valueState = atom({
  key: "fundKRValueState",
  default: 0,
});

// develop 은 DateRangeSubmitEvent 가 사실상 (any) => void 라 추론 타입으로도 통과했다.
// uschool 은 제대로 타입이 붙어 있어 atom 타입을 명시해야 한다.
export const fundKRChartDateState = atom<DateRangeType>({
  key: "fundKRChartDateState",
  default: {
    begin: null,
    end: null,
  },
});

export const enableFilterState = atom({
  key: "fundKREnableFitlerState", 
  default: false,
})

export const familyTickerFilterState = atom({
  key: "familyTickerFilterState", 
  default: false,
})

export const selectedRowState = atom<AssetType|null>({
  key: "fundKRSelectedRow",
  default: null,
});

export const simpleOperationListState = selector({
  key: "fundKRSimpleOperationListState",
  get: async ({ get }) => {
    const response = await axios.get(`bts/fund/kr/simple_operations`);
    return response.data.data;
  },
});

export const paginationState = atom({
  key: "fundKRPaginationState",
  default: {
    page: 1,
    page_size: 100,
    total_count: 0,
  },
});

export const fundSearchTextState = atom({
  key: "fundKRSearchState",
  default: undefined,
});

export const fundKrSelectedQueryFilterState = atom({
  key: "fundKrSelectedQueryFilterState",
  default: {},
})

export const fundKrQueryFilterState = atom<any>({
  key: "fundKrQueryFilterState",
  default: undefined,
});

export const fundKrSelectedClass = atom<any>({
  key: "fundKrSelectedClass",
  default: {
    filter: [],
  },
});

/**
 * 목록 정렬. **서버로 보낸다.**
 *
 * 29,184 건을 100 씩 서버 페이징하는 목록이라 화면에서 정렬하면 지금 보고 있는 100 건만
 * 줄을 세운다. 실측으로 확인했다 — 헤더를 눌러도 서버 요청이 한 건도 나가지 않았고
 * 페이지 표시는 `1–100 of 29184` 그대로였다.
 *
 * `field` 는 **DB 컬럼명**이다. 서버가 화이트리스트로 검증하고 모르는 값이면 조용히
 * `ticker` 로 떨어진다. 화면 컬럼 이름(한글)과 다르므로 `define.ts` 의 매핑을 쓴다.
 */
export type FundKrSort = { field: string; order: "asc" | "desc" };

/** 서버 기본값과 같다. 화면도 이 상태로 시작해야 "정렬 안 된 것처럼" 보이지 않는다. */
export const FUND_KR_DEFAULT_SORT: FundKrSort = { field: "ticker", order: "asc" };

export const fundKrSortState = atom<FundKrSort>({
  key: "fundKrSortState",
  default: FUND_KR_DEFAULT_SORT,
});

export const fundOperationListState = selector({
  key: "fundKROperationListState",
  get: async ({ get }) => {
    const pagenation = get(paginationState);
    const search = get(fundSearchTextState);
    const filter = get(fundKrQueryFilterState);
    const familyTickerChecked = get(familyTickerFilterState);
    const sort = get(fundKrSortState);
    const sortParams = { orderBy: sort.field, order: sort.order };

    if (familyTickerChecked) {
      const params = { search, ...pagenation, filter, familyTickerChecked, ...sortParams };
      const response = await axios.get(`bts/fund/kr/operations`, {
        params: params,
      });
      return response.data;
    } else {
      const params = { search, ...pagenation, filter, ...sortParams };
      const response = await axios.get(`bts/fund/kr/operations`, {
        params: params,
      });
      return response.data;
    }
  },
});

const selectArtificailIndexData = async (ticker, params) => {
  try {
    const response = await axios.get(`bts/fund/kr/artificial_prices/${ticker}`, {
      params,
    });
    const responseValue = response.data.data;
    let indexDatareduce = reducePriceValue(responseValue);
    indexDatareduce = { ...indexDatareduce, is_missing: response.data.data.is_missing}
    return indexDatareduce
  } catch (e) {
    console.error(`/// fund/kr/artificial_prices/${ticker}`, e);
  }
  return;
};

export const artificailIndexDataState = selector({
  key: "fundKRArtificailIndexDataState",
  get: async ({ get }) => {
    const selected = get(selectedRowState);
    if (selected === null) return;
    const { ticker } = selected;
    const dataParam = get(fundKRChartDateState);
    return await selectArtificailIndexData(ticker, dataParam);
  },
});

export const analysisDataState = atom({
  key: "fundKRAnalysisDataState",
  default : {load:false}
})

export const fundKRFeeState = selector({
  key: "fundKRFeeState",
  get: async ({ get }) => {    
    try {      
      const selected = get(selectedRowState);
      if (selected === null) return;
      const { ticker } = selected;
      const response = await axios.get(`bts/fund/kr/fee/${ticker}/history/`);
      const responseObj = response.data.data;
      return responseObj;
    } catch (e) {
      return;
    }
  },
});

export const selectedAssetSummaryData = async (ticker) => {
  const response = await realAxios.all(
    ticker.map((v) => axios.get(`bts/fund/kr/operation/${v}`))
  );
  return response.map((res: any) => res.data.data);
};

export const fundKrOperationFilterCountState = selector({
  key: "fundKrOperationFilterCountState",
  get: async ({get}) => {
    const familyTickerChecked = get(familyTickerFilterState);
    const params = { familyTickerChecked };
    const response = await axios.get("bts/fund/kr/operation_filter_count", {
      params: params,
    });
    return response.data.data
  }
})